import { createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import { NotFound } from '~/components/NotFound'
import {
  RELOAD_KEY,
  RouteError,
  reloadForStaleChunk,
} from '~/components/RouteError'

export function getRouter() {
  if (typeof window !== 'undefined') {
    // Vite fires this when a lazy chunk fails to load (e.g. after a deploy).
    window.addEventListener('vite:preloadError', (event) => {
      if (reloadForStaleChunk()) event.preventDefault()
    })
    // Clear the one-reload guard once the fresh page has been up a while,
    // so a later deploy can trigger another reload (but a persistent failure
    // can't loop). `load` may already have fired by the time this runs.
    const clearGuardLater = () =>
      setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 10_000)
    if (document.readyState === 'complete') clearGuardLater()
    else window.addEventListener('load', clearGuardLater)
  }
  // One per router (so per prerendered page on the server, never shared
  // between requests); routes read it from the router context.
  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: 1 } },
  })
  return createRouter({
    routeTree,
    context: { queryClient },
    // Outermost, so the error, not-found and pending components are inside it.
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    ),
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: RouteError,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
